import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { provideComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { PasLayoutSidebarHolderComponent, PasSidebarHandleService } from '@porsche-aftersales/frontend-toolbox';
import { AgGridModule } from 'ag-grid-angular';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { filter, lastValueFrom, Observable } from 'rxjs';

import { TableViewService } from '../../../../app/services/reporting/table-view.service';
import { TableViewCustomModel } from '../../../../app/sub-component/table-view-administration/tva-custom/common/model/table-view-custom-model';
import {
  NOTIFICATION_UNEXPECTED_ERROR,
  TvaCustomSidebarComponent,
} from '../../../../app/sub-component/table-view-administration/tva-custom/component-sidebar/tva-custom-sidebar.component';
import { TvaCustomService } from '../../../../app/sub-component/table-view-administration/tva-custom/tva-custom.service';
import { PartsAgreementRestResponse } from '../../../api/tur-wr/models';
import { PartsAgreementService } from '../../../api/tur-wr/services';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { FieldMasterDataRoutingService } from '../../../services/details-view-service/supplier-parts-routing.service';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { TableViewAdministrationState } from '../../../sub-component/table-view-administration/common/enum/table-view-administration-state.enum';
import { TvaCustomSelectionComponent } from '../../../sub-component/table-view-administration/tva-custom/component-selection/tva-custom-selection.component';
import { TvaSessionService } from '../../../sub-component/table-view-administration/tva-session/tva-session.service';

import { ButtonCellRendererComponent } from './button-cell-renderer.component';
import { PartsAgreementEditorComponent } from './parts-agreement-editor.component';
import { PartsAgreementsStore } from './parts-agreements.store';

@Component({
  selector: 'app-parts-agreements',
  templateUrl: './parts-agreements.component.html',
  styleUrls: ['./parts-agreements.component.scss'],
  imports: [
    AsyncPipe,
    AgGridModule,
    TvaCustomSelectionComponent,
    TvaCustomSidebarComponent,
    TranslatePipe,
    PasLayoutSidebarHolderComponent,
    ButtonCellRendererComponent,
    GenericModalContentComponent,
    PartsAgreementEditorComponent,
  ],
  providers: [provideComponentStore(PartsAgreementsStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartsAgreementsComponent implements OnInit, AfterViewInit {
  private readonly fieldMasterDataRoutingService = inject(FieldMasterDataRoutingService);
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly partsAgreementControllerService = inject(PartsAgreementService);
  private readonly userService = inject(UserService);
  private readonly tableViewService = inject(TableViewService);
  private readonly tvaCustomService = inject(TvaCustomService);
  private readonly tvaSessionService = inject(TvaSessionService);
  private readonly partsAgreementsStore = inject(PartsAgreementsStore);

  @ViewChild('modalTemplate')
  public modalTemplate: ElementRef;

  public gridOptions: GridOptions;
  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public excelStyles: any;
  public multiSortKey: any;
  public frameworkComponents: any;
  public tooltipShowDelay = 0;

  public listTableViews: TableViewCustomModel[] = [];
  public numberOfButtonsVisibleSidebar = 1;

  public partsAgreementOverviewData: PartsAgreementRestResponse[] = [];

  private gridApi: GridApi;

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$: Observable<boolean> =
    this.partsAgreementsStore.isFormValidAndModalNotOpenInDisplayOnlyMode$;

  protected readonly canAddPartsAgreement: boolean;

  public constructor() {
    this.canAddPartsAgreement = this.userService.hasRight(UserRights.RECOURSE_BOOKING);
  }

  public ngOnInit(): void {
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      unSortIcon: true,
      floatingFilter: true,
    };

    this.translateService.get('partsAgreement.OVERVIEW_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('partsAgreement.id'),
          field: 'id',
          filter: 'agTextColumnFilter',
          minWidth: 50,
          maxWidth: 60,
        },
        {
          headerName: this.translateService.instant('partsAgreement.supplierNumberShort'),
          headerTooltip: this.translateService.instant('partsAgreement.supplierNumber'),
          field: 'supplierNumber',
          filter: 'agTextColumnFilter',
          minWidth: 80,
          maxWidth: 140,
        },
        {
          headerName: this.translateService.instant('partsAgreement.supplierNameShort'),
          headerTooltip: this.translateService.instant('partsAgreement.supplierName'),
          field: 'supplierName',
          filter: 'agTextColumnFilter',
          minWidth: 120,
        },
        {
          headerName: this.translateService.instant('partsAgreement.sparePartNumberShort'),
          headerTooltip: this.translateService.instant('partsAgreement.sparePartNumber'),
          field: 'sparePartNumber',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('partsAgreement.technicalFactorShort'),
          headerTooltip: this.translateService.instant('partsAgreement.technicalFactor'),
          field: 'technicalFactor',
          valueFormatter: (params: any) => (Number(params.value) * 100).toFixed(2),
          filter: 'agTextColumnFilter',
          minWidth: 80,
          maxWidth: 120,
        },
        {
          headerName: this.translateService.instant('partsAgreement.orderTypeShort'),
          headerTooltip: this.translateService.instant('partsAgreement.orderType'),
          field: 'orderType',
          filter: 'agTextColumnFilter',
          minWidth: 80,
          maxWidth: 140,
        },
        {
          headerName: this.translateService.instant('partsAgreement.productionDateFromShort'),
          headerTooltip: this.translateService.instant('partsAgreement.productionDateFrom'),
          field: 'productionDateFrom',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('partsAgreement.productionDateToShort'),
          headerTooltip: this.translateService.instant('partsAgreement.productionDateTo'),
          field: 'productionDateTo',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('partsAgreement.modelYearFromShort'),
          headerTooltip: this.translateService.instant('partsAgreement.modelYearFrom'),
          field: 'modelYearFrom',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('partsAgreement.modelYearToShort'),
          headerTooltip: this.translateService.instant('partsAgreement.modelYearTo'),
          field: 'modelYearTo',
          filter: 'agTextColumnFilter',
        },

        {
          headerName: this.translateService.instant('partsAgreement.validityDateFromShort'),
          headerTooltip: this.translateService.instant('partsAgreement.validityDateFrom'),
          field: 'validityDateFrom',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('partsAgreement.validityDateToShort'),
          headerTooltip: this.translateService.instant('partsAgreement.validityDateTo'),
          field: 'validityDateTo',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          cellRenderer: ButtonCellRendererComponent,
          filter: false,
          menuTabs: [],
          sortable: false,
          width: 100,
          minWidth: 100,
          suppressSizeToFit: true,
        },
      ];
    });

    this.multiSortKey = 'ctrl';
    this.enableSidebar();
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.gridApi = event.api;

    this.gridApi.updateGridOptions({
      headerHeight: 32,
    });
    event.api.applyColumnState({
      state: [{ colId: 'id', sort: 'asc' }],
    });

    this.partsAgreementsStore.partsAgreements$
      .pipe(filter(data => data.length > 0))
      .subscribe(data => this.gridApi.updateGridOptions({ rowData: data }));

    this.tableViewService
      .getTableViewsFromRegisteredUserByTableViewArea(TableViewAdministrationState.PartsAgreementsOverview)
      .subscribe({
        next: async resultListTableView => {
          this.listTableViews = resultListTableView;
          this.partsAgreementOverviewData = await lastValueFrom(
            this.partsAgreementControllerService.retrievePartsAgreements(),
          );
          event.api.updateGridOptions({
            rowData: this.partsAgreementOverviewData,
          });

          this.tvaSessionService.init(
            TableViewAdministrationState.PartsAgreementsOverview,
            false,
            this.gridOptions,
            this.tvaCustomService,
            this.gridApi,
          );

          this.tvaCustomService.init(
            TableViewAdministrationState.PartsAgreementsOverview,
            this.listTableViews,
            false,
            this.gridOptions,
            this.gridApi,
          );
          if (!this.tvaSessionService.isTableViewFromTableViewSessionAvailable) {
            this.tvaCustomService.doSelectedTableViewOnComponent();
          }
        },
        error: () => {
          this.tvaCustomService.tvaCustomSidebarComponent.showNotification(NOTIFICATION_UNEXPECTED_ERROR, null);
        },
      });
  }

  public ngAfterViewInit(): void {
    this.partsAgreementsStore.patchState({ modalTemplate: this.modalTemplate });
  }

  public onBtnAddSupplierPartsAgreement(): void {
    this.fieldMasterDataRoutingService.routeToTabCreateFactor();
  }

  public onConfirm(): void {
    this.partsAgreementsStore.saveOrUpdatePartsAgreement();
  }

  public onCancel(): void {
    this.partsAgreementsStore.cancelPartsAgreementEditing();
  }

  private enableSidebar(): void {
    this.sidebarHandleService.enableSidebarForView();
  }
}
